import React from 'react';
import { mediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  MapPin, Phone, Mail, Clock, ImageIcon, BookOpen, UtensilsCrossed,
  CalendarPlus, Map as MapIcon, ChefHat, Star,
} from 'lucide-react';

const eur = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const img = (u) => (u ? mediaUrl(u) : '');

const WEEK = [
  ['mon', 'Lun'], ['tue', 'Mar'], ['wed', 'Mer'], ['thu', 'Gio'],
  ['fri', 'Ven'], ['sat', 'Sab'], ['sun', 'Dom'],
];
const TODAY_KEY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][new Date().getDay()];

function DishCard({ d }) {
  return (
    <article className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md" data-testid={`rl-dish-${d.id}`}>
      <div className="relative h-40 overflow-hidden bg-muted">
        {d.image_url ? (
          <img src={img(d.image_url)} alt={d.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><UtensilsCrossed className="h-8 w-8 text-muted-foreground" /></div>
        )}
        {d.dish_of_day && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground"><Star className="h-3 w-3" />Piatto del giorno</span>
        )}
      </div>
      <div className="p-4">
        {d.category && <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{d.category}</p>}
        <h4 className="mt-0.5 font-display text-base font-semibold text-foreground">{d.name}</h4>
        {d.description && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{d.description}</p>}
        {d.price > 0 && <p className="mt-2 font-display text-lg font-bold text-foreground">{eur(d.price)}</p>}
      </div>
    </article>
  );
}

/**
 * Vista “vetrina” di un ristorante — presentazionale e riusabile.
 * Usata sia dalla pagina pubblica (/ristorante/:id) sia dall'anteprima nel CRM.
 */
export function RestaurantLandingView({ data, publicMode = false, onViewMenu, onBook }) {
  const d = data || {};
  const gallery = (d.gallery || []).filter(Boolean);
  const highlights = d.menu_highlights || [];
  const hours = d.hours || null;
  const lat = d.lat;
  const lng = d.lng;
  const hasGeo = (lat != null && lat !== '' && lng != null && lng !== '') || !!d.address;
  const q = (lat != null && lat !== '' && lng != null && lng !== '')
    ? `${lat},${lng}` : encodeURIComponent(d.address || d.name || '');
  const mapSrc = `https://maps.google.com/maps?q=${q}&z=15&hl=it&output=embed`;

  return (
    <div data-testid="restaurant-landing-view">
      {/* HERO */}
      <section className="relative isolate flex min-h-[46vh] items-end overflow-hidden rounded-3xl text-white">
        {d.hero_image ? (
          <img src={img(d.hero_image)} alt={d.name} className="absolute inset-0 -z-10 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 -z-10 bg-primary/80" />
        )}
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(180deg, hsl(150 42% 8% / 0.25) 0%, hsl(150 42% 8% / 0.55) 55%, hsl(150 42% 8% / 0.92) 100%)' }} />
        <div className="w-full p-6 sm:p-10">
          {d.tagline && (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide backdrop-blur-md">{d.tagline}</span>
          )}
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold leading-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)] sm:text-5xl" data-testid="restaurant-name">{d.name || 'Ristorante'}</h1>
          {d.address && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-white/90"><MapPin className="h-4 w-4" />{d.address}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {d.has_menu && (
              <button type="button" onClick={() => onViewMenu && onViewMenu()} disabled={!onViewMenu}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60" data-testid="restaurant-cta-menu">
                <BookOpen className="h-4 w-4" />Vedi il menu
              </button>
            )}
            {d.reservations_enabled && (
              <button type="button" onClick={() => onBook && onBook()} disabled={!onBook}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/20 disabled:opacity-60" data-testid="restaurant-cta-book">
                <CalendarPlus className="h-4 w-4" />Prenota un tavolo
              </button>
            )}
            {d.email && (
              <a href={`mailto:${d.email}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/20">
                <Mail className="h-4 w-4" />Contatta
              </a>
            )}
          </div>
        </div>
      </section>

      {/* INFO RAPIDE */}
      {(d.phone || d.email || (hours && hours[TODAY_KEY])) && (
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 rounded-2xl border border-border bg-card px-5 py-3 text-sm">
          {hours && hours[TODAY_KEY] && (
            <span className="flex items-center gap-1.5 text-foreground"><Clock className="h-4 w-4 text-primary" />
              {hours[TODAY_KEY].active
                ? `Oggi: ${(hours[TODAY_KEY].turns || []).map((t) => `${t.start}–${t.end}`).join(' · ') || 'Aperto'}`
                : 'Oggi: chiuso'}
            </span>
          )}
          {d.phone && <span className="flex items-center gap-1.5 text-foreground"><Phone className="h-4 w-4 text-primary" />{d.phone}</span>}
          {d.email && <span className="flex items-center gap-1.5 text-foreground"><Mail className="h-4 w-4 text-primary" />{d.email}</span>}
        </div>
      )}

      {/* STORIA */}
      {d.story && (
        <section className="mt-8" data-testid="restaurant-story">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><BookOpen className="h-5 w-5 text-primary" />La nostra storia</h2>
          <p className="mt-3 max-w-3xl whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">{d.story}</p>
        </section>
      )}

      {/* MENU IN EVIDENZA */}
      {highlights.length > 0 && (
        <section className="mt-10" data-testid="restaurant-highlights">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><ChefHat className="h-5 w-5 text-primary" />Dal nostro menu</h2>
            {d.has_menu && onViewMenu && (
              <Button variant="outline" className="rounded-full" onClick={() => onViewMenu()} data-testid="restaurant-view-full-menu">Vedi il menu completo</Button>
            )}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {highlights.map((h) => <DishCard key={h.id} d={h} />)}
          </div>
        </section>
      )}

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="mt-10" data-testid="restaurant-gallery">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><ImageIcon className="h-5 w-5 text-primary" />Immagini</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {gallery.map((g, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden rounded-xl bg-muted">
                <img src={img(g)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ORARI */}
      {hours && (
        <section className="mt-10" data-testid="restaurant-hours">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><Clock className="h-5 w-5 text-primary" />Orari di apertura</h2>
          <div className="mt-4 overflow-hidden rounded-2xl border border-border">
            {WEEK.map(([k, label]) => {
              const day = hours[k] || { active: false, turns: [] };
              const isToday = k === TODAY_KEY;
              return (
                <div key={k} className={`flex items-center justify-between px-4 py-2.5 text-sm ${isToday ? 'bg-primary/5 font-semibold' : ''} border-b border-border last:border-0`}>
                  <span className="flex items-center gap-2 text-foreground">{label}{isToday && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">oggi</span>}</span>
                  <span className="text-muted-foreground">
                    {day.active && (day.turns || []).length
                      ? (day.turns || []).map((t) => `${t.start}–${t.end}`).join(' · ')
                      : 'Chiuso'}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* MAPPA */}
      {hasGeo && (
        <section className="mt-10" data-testid="restaurant-map">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><MapIcon className="h-5 w-5 text-primary" />Dove siamo</h2>
          <div className="mt-4 overflow-hidden rounded-2xl border border-border">
            <iframe title="Mappa ristorante" src={mapSrc} className="h-[360px] w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
          {d.address && <p className="mt-2 text-sm text-muted-foreground">{d.address}</p>}
        </section>
      )}

      {(highlights.length === 0 && !d.story && gallery.length === 0) && (
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-muted/40 p-8 text-center text-sm text-muted-foreground" data-testid="restaurant-empty">
          Questo ristorante sta completando la sua vetrina. Torna presto!
        </div>
      )}
    </div>
  );
}

export default RestaurantLandingView;
